import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    AssignmentTurnedIn,
    ChevronRight,
    Delete,
    Drafts,
    LocalShipping,
    Markunread,
    Search as SearchIcon
} from '@mui/icons-material';

import { Header } from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';

import { useDispatch, useSelector } from '../../redux/hooks';
import { fetchAllNotifications, selectAllNotifications } from '../../redux/actions/notifications';

import { NotificationType } from '../../types';
import {
    deleteNotifications,
    setReadNotifications,
    setUnreadNotifications
} from '../../api/notifications';
import toast from '../../utils/toast';
import { formatDateTime } from '../../utils/parsing';
import { getTenantLinkForNotifications } from '../../utils/tenant-links';
import {
    COLOR_BLUE,
    COLOR_GREY_DARK,
    COLOR_MINT_LIGHT,
    COLOR_OUTLINE,
    COLOR_RED,
    COLOR_TEAL_SURFACE,
    COLOR_WHITEISH
} from '../../theme';

import NotificationEventIcon from './NotificationEventIcon';
import {
    buildNotificationGroups,
    filterNotificationGroups,
    formatGroupRoute,
    NotificationGroup,
    NotificationSort,
    parseOrderIdFromMessage,
    sortNotificationGroups
} from './groupNotifications';

function parseMessage(notification: NotificationType) {
    const targets = [
        'har skickat ett nytt meddelande',
        'har lämnat ett nytt prisförslag',
        'har accepterat ert prisförslag',
        'har avböjt ert prisförslag',
        'har dragit tillbaka sitt prisförslag',
        'har bekräftat uppdrag',
        'har utfört uppdrag',
        'har laddat upp'
    ];
    const matchedTarget = targets.find((target) => notification.message.includes(target));

    if (matchedTarget) {
        const idx = notification.message.indexOf(matchedTarget);
        const rest = notification.message.slice(idx).replace(/\shttps?:\/\/\S+$/, '');
        return (
            <>
                {getTenantLinkForNotifications(notification)}
                {' '}
                {rest}
            </>
        );
    }
    return notification.message;
}

function CountPill({
    label,
    variant
}: {
    label: string,
    variant: 'total' | 'unread'
}) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.25,
                py: 0.35,
                borderRadius: 10,
                fontSize: '0.8125rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                bgcolor: variant === 'unread' ? COLOR_MINT_LIGHT : COLOR_TEAL_SURFACE,
                color: COLOR_BLUE
            }}
        >
            {label}
        </Box>
    );
}

function StatusBadge({
    isRead,
    onToggle
}: {
    isRead: boolean,
    onToggle: () => void
}) {
    return (
        <Box
            component="button"
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onToggle();
            }}
            aria-label={isRead ? 'Markera som oläst' : 'Markera som läst'}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.25,
                py: 0.25,
                borderRadius: 10,
                border: `1px solid ${COLOR_OUTLINE}`,
                bgcolor: 'transparent',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                color: COLOR_GREY_DARK,
                textTransform: 'uppercase',
                flexShrink: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                lineHeight: 1.5,
                transition: 'background-color 0.15s ease',
                '&:hover': {
                    bgcolor: alpha(COLOR_OUTLINE, 0.28)
                }
            }}
        >
            {isRead ? 'Läst' : 'Oläst'}
        </Box>
    );
}

export default function Notifications() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const notifications = useSelector(selectAllNotifications());

    const [search, setSearch] = React.useState('');
    const [sort, setSort] = React.useState<NotificationSort>('orderIdDesc');
    const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());
    const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(new Set());

    React.useEffect(() => {
        dispatch(fetchAllNotifications());
    }, [dispatch]);

    const allGroups = React.useMemo(
        () => buildNotificationGroups(notifications).filter(
            (group) => group.isMisc || group.isViewable
        ),
        [notifications]
    );

    const groups = React.useMemo(() => {
        const filtered = filterNotificationGroups(allGroups, search);
        return sortNotificationGroups(filtered, sort);
    }, [allGroups, search, sort]);

    const totalUnread = React.useMemo(
        () => allGroups.reduce((sum, group) => sum + group.unreadCount, 0),
        [allGroups]
    );

    const uppdragCount = React.useMemo(
        () => allGroups.filter((group) => !group.isMisc).length,
        [allGroups]
    );
    const summary = `${uppdragCount} uppdrag · ${totalUnread} ${totalUnread === 1 ? 'oläst händelse' : 'olästa händelser'}`;

    const visibleSelectedGroups = React.useMemo(
        () => groups.filter((g) => selectedKeys.has(g.key)),
        [groups, selectedKeys]
    );

    const selectedNotifications = React.useMemo(
        () => visibleSelectedGroups.flatMap((g) => g.notifications),
        [visibleSelectedGroups]
    );

    const visibleSelectedCount = visibleSelectedGroups.length;

    const toggleExpanded = React.useCallback((key: string) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const toggleSelected = React.useCallback((key: string) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const markAsRead = React.useCallback(async (
        selected: NotificationType[],
        muteToast?: boolean
    ) => {
        try {
            await setReadNotifications(selected.map((n) => n.id));
            dispatch(fetchAllNotifications());
            if (selected.length === 1 && muteToast !== true) {
                toast('1 notis markerad som läst');
            } else if (muteToast !== true) {
                toast(`${selected.length} händelser markerade som lästa`);
            }
        } catch (e) {
            toast(e, 'Kunde inte markera händelser som lästa, försök igen');
        }
    }, [dispatch]);

    const markAsUnread = React.useCallback(async (selected: NotificationType[]) => {
        try {
            await setUnreadNotifications(selected.map((n) => n.id));
            dispatch(fetchAllNotifications());
            if (selected.length === 1) {
                toast('1 notis markerad som oläst');
            } else {
                toast(`${selected.length} händelser markerade som olästa`);
            }
        } catch (e) {
            toast(e, 'Kunde inte markera händelser som olästa, försök igen');
        }
    }, [dispatch]);

    const removeNotifications = React.useCallback(async (selected: NotificationType[]) => {
        try {
            await deleteNotifications(selected.map((n) => n.id));
            dispatch(fetchAllNotifications());
            if (selected.length === 1) {
                toast('1 notis borttagen');
            } else {
                toast(`${selected.length} händelser borttagna`);
            }
        } catch (e) {
            toast(e, 'Kunde inte ta bort händelser, försök igen');
        }
    }, [dispatch]);

    const runBulk = React.useCallback(async (
        action: (selected: NotificationType[]) => Promise<void>
    ) => {
        if (!selectedNotifications.length) {
            return;
        }
        const actedKeys = new Set(visibleSelectedGroups.map((g) => g.key));
        await action(selectedNotifications);
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            actedKeys.forEach((key) => next.delete(key));
            return next;
        });
    }, [selectedNotifications, visibleSelectedGroups]);

    const navigateTo = React.useCallback((notification: NotificationType) => {
        const {
            orderId,
            offerId,
            jobId,
            groupId,
            capacityId,
            watchlistId,
            isRead
        } = notification;
        const parsedUppdragId = parseOrderIdFromMessage(notification.message);

        if (orderId || jobId || groupId || capacityId || watchlistId || parsedUppdragId) {
            if (!isRead) {
                markAsRead([notification], true);
            }
        } else if (!isRead) {
            toast('Objektet är inte tillgängligt - händelsen markerad som läst');
            markAsRead([notification], true);
        } else {
            toast('Objektet är inte tillgängligt');
        }

        if (orderId && offerId) {
            navigate(`/orders/${orderId}?ooid=${offerId}`);
        } else if (orderId) {
            navigate(`/orders/${orderId}`);
        } else if (jobId) {
            navigate(`/jobs/${jobId}`);
        } else if (parsedUppdragId) {
            navigate(`/jobs/${parsedUppdragId}`);
        } else if (groupId) {
            navigate(`/groups/${groupId}`);
        } else if (capacityId) {
            navigate(`/capacities/${capacityId}?o=true`);
        } else if (watchlistId) {
            navigate(`/watchlists/${watchlistId}`);
        }
    }, [markAsRead, navigate]);

    const toggleReadStatus = React.useCallback((notification: NotificationType) => {
        if (notification.isRead) {
            markAsUnread([notification]);
        } else {
            markAsRead([notification]);
        }
    }, [markAsRead, markAsUnread]);

    const navigateToGroup = React.useCallback((group: NotificationGroup) => {
        if (!group.isViewable || group.orderOrJobId == null) {
            return;
        }
        if (group.isMyOrder) {
            navigate(`/orders/${group.orderOrJobId}`);
        } else {
            navigate(`/jobs/${group.orderOrJobId}`);
        }
    }, [navigate]);

    const renderGroup = (group: NotificationGroup) => {
        const expanded = expandedKeys.has(group.key);
        const selected = selectedKeys.has(group.key);
        const route = formatGroupRoute(group);
        const title = group.isMisc
            ? 'Övrigt'
            : `#${group.orderOrJobId}`;
        // eslint-disable-next-line no-nested-ternary
        const companyLabel = group.isMisc
            ? null
            : (group.isMyOrder ? 'Mitt uppdrag' : group.tenantName);
        const subtitlePrimary = group.isMisc
            ? group.tenantName
            : (route || companyLabel || '');
        const subtitleSecondary = !group.isMisc && route ? companyLabel : null;

        return (
            <Box
                key={group.key}
                sx={{
                    borderBottom: `1px solid ${COLOR_WHITEISH}`,
                    '&:last-child': { borderBottom: 'none' }
                }}
            >
                <Box
                    onClick={() => toggleExpanded(group.key)}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 1,
                        px: { xs: 1, sm: 2 },
                        py: 2.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: COLOR_WHITEISH }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    <Checkbox
                        checked={selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelected(group.key)}
                        size="small"
                        inputProps={{ 'aria-label': `Välj ${title}` }}
                    />
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(group.key);
                        }}
                        aria-label={expanded ? 'Fäll ihop' : 'Expandera'}
                        sx={{
                            transition: 'transform 200ms ease',
                            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                            flexWrap: 'wrap'
                        }}
                        >
                            <Typography
                                component="span"
                                sx={{ fontWeight: 700, color: COLOR_BLUE, fontSize: '1rem' }}
                            >
                                {title}
                            </Typography>
                            {subtitlePrimary && (
                                <Typography component="span" sx={{ color: COLOR_BLUE }}>
                                    {subtitlePrimary}
                                </Typography>
                            )}
                        </Box>
                        {subtitleSecondary && (
                            <Typography variant="body2" sx={{ color: COLOR_GREY_DARK }}>
                                {subtitleSecondary}
                            </Typography>
                        )}
                    </Box>
                    {group.isViewable && group.orderOrJobId != null && (
                        <Tooltip title="Gå till uppdrag">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToGroup(group);
                                }}
                                aria-label="Gå till uppdrag"
                                sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
                            >
                                {group.isMyOrder
                                    ? <AssignmentTurnedIn color="action" />
                                    : <LocalShipping color="action" />}
                            </IconButton>
                        </Tooltip>
                    )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                            pl: { xs: 11.5, md: 0 },
                            ml: { md: 'auto' },
                            flexShrink: 0
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: COLOR_GREY_DARK,
                                whiteSpace: 'nowrap',
                                textAlign: 'left',
                                fontVariantNumeric: 'tabular-nums',
                                overflow: 'hidden'
                            }}
                        >
                            {formatDateTime(group.latestCreatedAt)}
                        </Typography>
                        <CountPill
                            label={`${group.totalCount} händelser`}
                            variant="total"
                        />
                        {group.unreadCount > 0 && (
                            <CountPill
                                label={`${group.unreadCount} ${group.unreadCount === 1 ? 'oläst' : 'olästa'}`}
                                variant="unread"
                            />
                        )}
                        {group.isViewable && group.orderOrJobId != null ? (
                            <Tooltip title="Gå till uppdrag">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigateToGroup(group);
                                    }}
                                    aria-label="Gå till uppdrag"
                                    sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                                >
                                    {group.isMyOrder
                                        ? <AssignmentTurnedIn color="action" />
                                        : <LocalShipping color="action" />}
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Box sx={{ width: 40, height: 40, display: { xs: 'none', md: 'block' } }} aria-hidden />
                        )}
                    </Box>
                </Box>

                <Collapse in={expanded} timeout={220} unmountOnExit>
                    <Box
                        sx={{
                            bgcolor: COLOR_TEAL_SURFACE,
                            px: { xs: 1, sm: 2 },
                            py: 1,
                            pl: { xs: 2, sm: 9 }
                        }}
                    >
                        {group.notifications.map((notification, index) => {
                            const isLast = index === group.notifications.length - 1;
                            return (
                                <Box
                                    key={notification.id}
                                    onClick={() => navigateTo(notification)}
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        pt: 1,
                                        pb: 0.5,
                                        cursor: 'pointer',
                                        position: 'relative',
                                        '&:hover': { opacity: 0.9 }
                                    }}
                                >
                                    <Box sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}
                                    >
                                        {!isLast && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 36,
                                                    bottom: -20,
                                                    width: '1px',
                                                    bgcolor: COLOR_OUTLINE
                                                }}
                                            />
                                        )}
                                        <NotificationEventIcon notification={notification} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                color: COLOR_BLUE,
                                                fontSize: '0.95rem',
                                                fontWeight: notification.isRead ? 400 : 600
                                            }}
                                        >
                                            {parseMessage(notification)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: COLOR_GREY_DARK, mt: 0.25 }}>
                                            {formatDateTime(notification.createdAt)}
                                            {!notification.isRead ? ' · Oläst' : ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, mt: { xs: 0.5, sm: 0 } }}>
                                        <StatusBadge
                                            isRead={notification.isRead}
                                            onToggle={() => toggleReadStatus(notification)}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Collapse>
            </Box>
        );
    };

    return (
        <Container>
            <Header title="Händelser" subtitle={notifications.length ? summary : undefined} />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mb: 2,
                    alignItems: { sm: 'center' }
                }}
            >
                <TextField
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Sök uppdrags-id, ort, motpart eller meddelande"
                    fullWidth
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        )
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="notifications-sort-label">Sortera</InputLabel>
                    <Select
                        labelId="notifications-sort-label"
                        label="Sortera"
                        value={sort}
                        onChange={(e: SelectChangeEvent) => {
                            setSort(e.target.value as NotificationSort);
                        }}
                    >
                        <MenuItem value="orderIdDesc">Uppdrags-id (högst först)</MenuItem>
                        <MenuItem value="latestActivity">Senaste händelse</MenuItem>
                        <MenuItem value="unreadFirst">Olästa först</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Paper padding={0}>
                {!notifications.length ? (
                    <Box sx={{ p: 3 }}>
                        <Typography>Du har inga händelser.</Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                flexWrap: 'wrap',
                                alignItems: { xs: 'stretch', sm: 'center' },
                                justifyContent: 'space-between',
                                gap: 1,
                                px: 2,
                                py: 1.5,
                                borderBottom: `1px solid ${COLOR_WHITEISH}`
                            }}
                        >
                            <Typography variant="body2" sx={{ color: COLOR_GREY_DARK }}>
                                {visibleSelectedCount === 0 && 'Välj uppdrag att hantera'}
                                {visibleSelectedCount === 1 && '1 uppdrag valt'}
                                {visibleSelectedCount > 1 && `${visibleSelectedCount} uppdrag valda`}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Markunread />}
                                    disabled={!visibleSelectedCount}
                                    onClick={() => runBulk(markAsUnread)}
                                    sx={{ py: 1, px: 2 }}
                                >
                                    Markera som oläst
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Drafts />}
                                    disabled={!visibleSelectedCount}
                                    onClick={() => runBulk(markAsRead)}
                                    sx={{ py: 1, px: 2 }}
                                >
                                    Markera som läst
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<Delete />}
                                    disabled={!visibleSelectedCount}
                                    onClick={() => runBulk(removeNotifications)}
                                    sx={{ py: 1, px: 2, color: COLOR_RED }}
                                >
                                    Ta bort
                                </Button>
                            </Box>
                        </Box>
                        {!groups.length ? (
                            <Box sx={{ p: 3 }}>
                                <Typography>
                                    {search.trim()
                                        ? 'Inga händelser matchar sökningen.'
                                        : 'Inga händelser att visa.'}
                                </Typography>
                            </Box>
                        ) : (
                            <Box key={sort}>
                                {groups.map(renderGroup)}
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Container>
    );
}
